// Lead Collector — on-demand Google Places runs plus email/director enrichment.
// (The 06:00 cron automates the daily Places discovery.)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapPinned, Play, RefreshCw, Mail, Users, Globe, Building2 } from 'lucide-react';
import { apiService } from '../services/apiService';
import { Card, StatCard, Chip, Btn, inputStyle, useToasts, Spinner, fmtNum } from './ui';

const LeadCollector = () => {
  const [stats, setStats] = useState(null);
  const [job, setJob] = useState(null);
  const [queries, setQueries] = useState('');
  const [area, setArea] = useState('');
  const [enrichLimit, setEnrichLimit] = useState(100);
  const [push, toasts] = useToasts();
  const pollRef = useRef(null);

  const loadStats = useCallback(async () => {
    try {
      const [s] = await Promise.allSettled([apiService.getLeadStats()]);
      if (s.status === 'fulfilled' && !s.value.error) setStats(s.value);
    } catch { /* stats are non-critical */ }
  }, []);

  const pollStatus = useCallback(async () => {
    try {
      const st = await apiService.getLeadStatus();
      setJob(st);
      if (!st.running) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        loadStats();
      }
    } catch { /* keep last known */ }
  }, [loadStats]);

  useEffect(() => {
    loadStats();
    pollStatus();
    return () => clearInterval(pollRef.current);
  }, [loadStats, pollStatus]);

  const startPolling = () => {
    if (!pollRef.current) pollRef.current = setInterval(pollStatus, 5000);
  };

  const run = async (fn, label) => {
    try {
      const res = await fn();
      if (res.error) {
        push(res.error, 'error');
        return;
      }
      push(`${label} started`);
      setJob({ running: true, task: res.task, message: 'Starting…' });
      startPolling();
    } catch (e) {
      push(`${label} failed: ${e.message}`, 'error');
    }
  };

  const runGoogle = () => {
    const list = queries.split('\n').map((q) => q.trim()).filter(Boolean);
    if (!list.length) { push('Add at least one query, e.g. "plumbers in Sandton"', 'error'); return; }
    run(() => apiService.runLeadsGoogle(list, area || undefined), 'Google Places scrape');
  };

  const busy = !!job?.running;

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 20px 60px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 22, fontWeight: 700, color: 'var(--c-text)' }}>Lead Collector</div>
          <div style={{ fontSize: 13, color: 'var(--c-text-2)', marginTop: 3 }}>
            Business discovery via Google Places — the 06:00 job runs this automatically every morning.
          </div>
        </div>
        <Btn small onClick={() => { loadStats(); pollStatus(); }}><RefreshCw size={13} /> Refresh</Btn>
      </div>

      {/* Running job banner */}
      {job && (busy || job.error) && (
        <Card style={{
          marginBottom: 14, display: 'flex', alignItems: 'center', gap: 14,
          borderColor: busy ? 'var(--c-border-bright)' : 'rgba(239,68,68,0.4)',
        }}>
          {busy && <Spinner size={18} />}
          <div style={{ fontSize: 13, color: 'var(--c-text)' }}>
            {busy
              ? <><strong>{job.task}</strong> running — {job.message || '…'} {job.stored ? `(${fmtNum(job.stored)} stored)` : ''}</>
              : <span style={{ color: 'var(--c-red)' }}>Last job error: {job.error}</span>}
          </div>
        </Card>
      )}

      {/* Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 14 }}>
          <StatCard icon={Building2} label="Businesses" value={fmtNum(stats.businesses ?? stats.total ?? 0)} />
          <StatCard icon={Mail} label="With email" value={fmtNum(stats.businesses_with_email ?? stats.with_email ?? 0)} color="var(--c-blue)" />
          <StatCard icon={Globe} label="With website" value={fmtNum(stats.with_website ?? 0)} color="var(--c-purple)" />
          <StatCard icon={Users} label="With phone" value={fmtNum(stats.with_phone ?? 0)} color="var(--c-yellow)" />
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14 }}>
        {/* Google Places */}
        <Card>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--c-text)', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <MapPinned size={16} style={{ color: 'var(--c-primary)' }} /> Google Places
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--c-text-2)', marginBottom: 10 }}>
            One search per line, e.g. <em>plumbers in Sandton</em>. Results land in Records with source <Chip tone="blue">google</Chip>.
          </div>
          <textarea
            value={queries}
            onChange={(e) => setQueries(e.target.value)}
            rows={4}
            placeholder={'plumbers in Sandton\nattorneys in Pretoria East'}
            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', resize: 'vertical', marginBottom: 10 }}
          />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              value={area}
              onChange={(e) => setArea(e.target.value)}
              placeholder="Area tag (optional)"
              style={{ ...inputStyle, flex: 1 }}
            />
            <Btn tone="primary" onClick={runGoogle} disabled={busy}><Play size={13} /> Run</Btn>
          </div>
        </Card>

        {/* Enrichment */}
        <Card>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--c-text)', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <Mail size={16} style={{ color: 'var(--c-blue)' }} /> Enrichment
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--c-text-2)', marginBottom: 12 }}>
            Fill the gaps on collected businesses: crawl their sites for real email addresses, verify guessed role addresses, or pull company directors.
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 12.5, color: 'var(--c-text-3)' }}>Batch limit</span>
            <input
              type="number" min={10} max={1000} value={enrichLimit}
              onChange={(e) => setEnrichLimit(Number(e.target.value) || 100)}
              style={{ ...inputStyle, width: 90 }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Btn onClick={() => run(() => apiService.runLeadsEnrich('enrich-emails', enrichLimit), 'Email crawl')} disabled={busy}>
              <Play size={13} /> Crawl websites for emails
            </Btn>
            <Btn onClick={() => run(() => apiService.runLeadsEnrich('guess-emails', enrichLimit), 'Email guessing')} disabled={busy}>
              <Play size={13} /> Guess + verify role emails
            </Btn>
            <Btn onClick={() => run(() => apiService.runLeadsEnrich('enrich-directors', enrichLimit), 'Director lookup')} disabled={busy}>
              <Play size={13} /> Look up company directors
            </Btn>
          </div>
        </Card>
      </div>

      {toasts}
    </div>
  );
};

export default LeadCollector;
